import PropTypes from "prop-types";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Menu, Drawer } from "antd";
import Icon, { RightOutlined, LeftOutlined } from "@ant-design/icons";

// project import
import MenuItems from "menu-items/index";
import Logo from "../../../assets/images/Logo.png";
import LogoMinimised from "../../../assets/images/Logo(Minimised).png";

// Helper functions defined outside component
const findUrl = (items, targetKey) => {
  for (const item of items) {
    if (item.key === targetKey && item.url) {
      return item.url;
    }
    if (item.children) {
      const found = findUrl(item.children, targetKey);
      if (found) return found;
    }
  }
  return null;
};

const findKeyByUrl = (items, targetUrl) => {
  for (const item of items) {
    if (item.url === targetUrl) {
      return item.key;
    }
    if (item.children) {
      const found = findKeyByUrl(item.children, targetUrl);
      if (found) return found;
    }
  }
  return null;
};

const findParentKeys = (items, targetKey, parents = []) => {
  for (const item of items) {
    if (item.key === targetKey) {
      return parents;
    }
    if (item.children) {
      const found = findParentKeys(item.children, targetKey, [
        ...parents,
        item.key,
      ]);
      if (
        found.length > 0 ||
        item.children.some((child) => child.key === targetKey)
      ) {
        return [...parents, item.key];
      }
    }
  }
  return [];
};

// ==============================|| SIDEBAR ||============================== //

const Sidebar = ({ collapsed, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { company_logo, theme_color } = useSelector(
    (state) => state.company_profile
  );
  const menuItems = MenuItems();

  // Convert menu items to Ant Design format - memoized
  const antdMenuItems = useMemo(() => {
    const convertMenuItem = (item) => {
      if (!item.showMenu) return null;

      if (item.children && item.children.length > 0) {
        const visibleChildren = item.children
          .filter((child) => child.showMenu)
          .map((child) => convertMenuItem(child))
          .filter(Boolean);

        if (visibleChildren.length === 0) return null;

        return {
          key: item.id,
          icon: <Icon component={item.icon} />,
          label: item.title,
          children: visibleChildren,
        };
      }
      return {
        key: item.id,
        icon: <Icon component={item.icon} />,
        label: item.title,
        url: item.url,
        external: item.external,
        target: item.target,
      };
    };

    const convertToAntdItems = (items) => {
      if (!items?.items) return [];

      return items.items
        .map((group) => {
          if (group.type === "group") {
            const visibleChildren = group.children
              ?.filter((child) => child.showMenu)
              ?.map((child) => convertMenuItem(child))
              ?.filter(Boolean);
            return {
              key: group.id,
              label: group.title,
              type: "group",
              children: visibleChildren,
            };
          }
          if (group.showMenu === false) return null;
          return convertMenuItem(group);
        })
        .filter(Boolean);
    };

    return convertToAntdItems(menuItems);
  }, [menuItems]);

  // Calculate selected keys directly from pathname - no useEffect needed
  const selectedKeys = useMemo(() => {
    const key = findKeyByUrl(antdMenuItems, location.pathname);
    return key ? [key] : [];
  }, [antdMenuItems, location.pathname]);

  // Calculate initial open keys based on selected item
  const defaultOpenKeys = useMemo(() => {
    if (selectedKeys.length > 0) {
      return findParentKeys(antdMenuItems, selectedKeys[0]);
    }
    return [];
  }, [antdMenuItems, selectedKeys]);

  // Helper to find menu item by key
  const findMenuItem = (items, targetKey) => {
    for (const item of items) {
      if (item.key === targetKey) {
        return item;
      }
      if (item.children) {
        const found = findMenuItem(item.children, targetKey);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle menu click
  const handleMenuClick = ({ key }) => {
    const menuItem = findMenuItem(antdMenuItems, key);
    if (menuItem && menuItem.url) {
      // Check if this is an external link
      if (menuItem.external) {
        // Open external link in new tab
        window.open(menuItem.url, menuItem.target ? '_blank' : '_self');
      } else {
        // Internal navigation
        navigate(menuItem.url);
      }

      // Close drawer when navigating (only when expanded)
      if (!collapsed) {
        onCollapse();
      }
    }
  };

  const themeColor = theme_color || "#1a1a1a";

  // Common menu styles
  const menuStyles = `
    .sidebar-menu.ant-menu-dark,
    .sidebar-menu.ant-menu-dark .ant-menu-sub,
    .sidebar-menu.ant-menu-dark .ant-menu-sub.ant-menu-inline {
      background: transparent !important;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-item-selected {
      background-color: rgba(59, 130, 246, 0.25) !important;
      border-radius: 6px;
      font-weight: 500;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-item-selected::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background-color: #3b82f6;
      border-radius: 0 2px 2px 0;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-item:hover:not(.ant-menu-item-selected) {
      background-color: rgba(255, 255, 255, 0.06) !important;
      border-radius: 6px;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-submenu-title:hover {
      background-color: rgba(255, 255, 255, 0.06) !important;
      border-radius: 6px;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
      background-color: rgba(59, 130, 246, 0.15) !important;
      border-radius: 6px;
    }
    .sidebar-menu.ant-menu-dark .ant-menu-item,
    .sidebar-menu.ant-menu-dark .ant-menu-submenu-title {
      margin: 4px 8px;
      width: calc(100% - 16px);
    }
    .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item {
      margin: 2px 8px;
      padding-left: 48px !important;
    }
    /* Center icons when sidebar is collapsed */
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-item,
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
      padding: 0 !important;
      padding-left: 20px !important;
      display: flex !important;
      align-items: center !important;
      position: relative !important;
    }
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-item .anticon,
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .anticon,
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-item svg,
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-title svg {
      margin: 0 !important;
      font-size: 18px !important;
      width: 18px !important;
      height: 18px !important;
      min-width: 18px !important;
      line-height: 1 !important;
    }
    /* Hide default arrow in collapsed sidebar */
    .sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-arrow {
      display: none !important;
    }

    /* ======================= POPOVER SUBMENU STYLES ======================= */

    /* Popup container positioning - minimal gap from sidebar */
    .ant-menu-submenu-popup {
      z-index: 1050 !important;
      padding-left: 4px !important;
    }

    /* Main popover menu container */
    .ant-menu-submenu-popup > .ant-menu {
      background-color: ${themeColor} !important;
      border-radius: 8px !important;
      box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.3), 0 3px 6px -4px rgba(0, 0, 0, 0.4) !important;
      min-width: 180px !important;
      padding: 4px !important;
      position: relative !important;
      overflow: visible !important;
      margin-left: 6px !important;
    }

    /* Triangular arrow pointer - attached to popover */
    .ant-menu-submenu-popup > .ant-menu::before {
      content: '';
      position: absolute;
      left: -6px;
      top: 14px;
      width: 0;
      height: 0;
      border-top: 7px solid transparent;
      border-bottom: 7px solid transparent;
      border-right: 6px solid ${themeColor};
    }

    /* Popover menu items */
    .ant-menu-submenu-popup .ant-menu-item {
      color: rgba(255, 255, 255, 0.85) !important;
      margin: 4px !important;
      padding: 10px 16px !important;
      border-radius: 6px !important;
      height: auto !important;
      line-height: 1.5 !important;
      position: relative !important;
    }

    /* Popover menu item hover state */
    .ant-menu-submenu-popup .ant-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.08) !important;
      color: #fff !important;
    }

    /* Popover menu item selected state - matching main menu style */
    .ant-menu-submenu-popup .ant-menu-item-selected {
      background-color: rgba(59, 130, 246, 0.25) !important;
      color: #fff !important;
      font-weight: 500 !important;
    }

    /* Left border indicator for selected item in popover */
    .ant-menu-submenu-popup .ant-menu-item-selected::after {
      content: '' !important;
      position: absolute !important;
      left: 0 !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      width: 3px !important;
      height: 60% !important;
      background-color: #3b82f6 !important;
      border-radius: 0 2px 2px 0 !important;
      opacity: 1 !important;
      border: none !important;
    }

    /* Override any default ::after styles */
    .ant-menu-submenu-popup .ant-menu-item::after {
      border: none !important;
    }

    /* Text color in popover */
    .ant-menu-submenu-popup .ant-menu-title-content {
      color: rgba(255, 255, 255, 0.85) !important;
    }

    .ant-menu-submenu-popup .ant-menu-item-selected .ant-menu-title-content {
      color: #fff !important;
    }

    /* ======================= END POPOVER STYLES ======================= */

    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
    /* Drawer sidebar styles */
    .sidebar-drawer .ant-drawer-body {
      padding: 0 !important;
      background-color: ${themeColor};
    }
    .sidebar-drawer .ant-drawer-header {
      display: none;
    }
    .sidebar-drawer .ant-drawer-content {
      background-color: ${themeColor};
    }
    .sidebar-drawer .ant-drawer-content-wrapper {
      box-shadow: 2px 0 8px rgba(0,0,0,0.15) !important;
    }
  `;

  // Sidebar content component
  const SidebarContent = ({ isExpanded }) => (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: themeColor }}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-center px-4 border-b border-white/10">
        <img
          src={
            isExpanded ? company_logo || Logo : company_logo || LogoMinimised
          }
          alt="Company Logo"
          className={`transition-all duration-300 ${
            isExpanded
              ? "w-full max-h-12 object-contain"
              : "w-10 h-10 object-contain"
          }`}
        />
      </div>

      {/* Navigation Menu */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        <Menu
          mode="inline"
          theme="dark"
          inlineCollapsed={!isExpanded}
          selectedKeys={selectedKeys}
          defaultOpenKeys={isExpanded ? defaultOpenKeys : []}
          onClick={handleMenuClick}
          items={antdMenuItems}
          className="border-r-0 sidebar-menu"
          style={{
            backgroundColor: "transparent",
          }}
          subMenuOpenDelay={0.1}
          subMenuCloseDelay={0.2}
          popupOffset={[0, 0]}
        />
      </div>

      {/* Toggle Button at Bottom */}
      <div
        className="h-12 flex items-center justify-center border-t border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onCollapse}
        style={{ lineHeight: 1 }}
      >
        {isExpanded ? (
          <LeftOutlined
            style={{
              fontSize: 14,
              color: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        ) : (
          <RightOutlined
            style={{
              fontSize: 14,
              color: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Sidebar as Drawer - expands from 80px to 260px */}
      <Drawer
        placement="left"
        open={true}
        mask={!collapsed}
        onClose={onCollapse}
        width={collapsed ? 80 : 260}
        className="sidebar-drawer"
        closable={false}
        styles={{
          body: { padding: 0, backgroundColor: themeColor },
          mask: { backgroundColor: "rgba(0,0,0,0.3)" },
        }}
        rootClassName="sidebar-drawer-root"
        zIndex={1001}
      >
        <SidebarContent isExpanded={!collapsed} />
      </Drawer>

      <style>{menuStyles}</style>
    </>
  );
};

Sidebar.propTypes = {
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
};

export default Sidebar;
